import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip, Button,
  List, ListItem, ListItemIcon, ListItemText, Divider, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Snackbar, Alert
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Add, Edit, Close, AdminPanelSettings, School, Person, FamilyRestroom, SupervisorAccount } from '@mui/icons-material';

const defaultRoles = [
  {
    id: 'super_admin',
    name: 'Super User',
    icon: <SupervisorAccount />,
    color: '#4a148c',
    description: 'Full system access including direct database operations, bulk changes, and system configuration.',
    users: 1,
    permissions: {
      'View all data': true,
      'Edit all data': true,
      'Delete records': true,
      'Bulk operations': true,
      'System config': true,
      'Manage users': true,
      'Manage roles': true,
      'View applications': true,
      'Approve/reject applications': true,
      'Manage finances': true,
      'View analytics': true,
      'Send communications': true,
      'Manage website': true,
      'Security settings': true,
    }
  },
  {
    id: 'admin',
    name: 'Administrator',
    icon: <AdminPanelSettings />,
    color: '#050979',
    description: 'School-wide administrative access. Can manage students, staff, applications, communications, and reports.',
    users: 3,
    permissions: {
      'View all data': true,
      'Edit all data': true,
      'Delete records': false,
      'Bulk operations': false,
      'System config': false,
      'Manage users': true,
      'Manage roles': false,
      'View applications': true,
      'Approve/reject applications': true,
      'Manage finances': true,
      'View analytics': true,
      'Send communications': true,
      'Manage website': true,
      'Security settings': false,
    }
  },
  {
    id: 'teacher',
    name: 'Teacher',
    icon: <School />,
    color: '#388e3c',
    description: 'Access to their own classes, student gradebooks, attendance, and parent messaging.',
    users: 12,
    permissions: {
      'View all data': false,
      'Edit all data': false,
      'Delete records': false,
      'Bulk operations': false,
      'System config': false,
      'Manage users': false,
      'Manage roles': false,
      'View applications': false,
      'Approve/reject applications': false,
      'Manage finances': false,
      'View analytics': false,
      'Send communications': true,
      'Manage website': false,
      'Security settings': false,
    }
  },
  {
    id: 'parent',
    name: 'Parent / Guardian',
    icon: <FamilyRestroom />,
    color: '#e64a19',
    description: 'View their child\'s grades, attendance, assignments, and communicate with teachers.',
    users: 198,
    permissions: {
      'View all data': false,
      'Edit all data': false,
      'Delete records': false,
      'Bulk operations': false,
      'System config': false,
      'Manage users': false,
      'Manage roles': false,
      'View applications': false,
      'Approve/reject applications': false,
      'Manage finances': false,
      'View analytics': false,
      'Send communications': true,
      'Manage website': false,
      'Security settings': false,
    }
  },
  {
    id: 'student',
    name: 'Student',
    icon: <Person />,
    color: '#0288d1',
    description: 'View their own grades, assignments, schedule, and attendance. Read-only access.',
    users: 234,
    permissions: {
      'View all data': false,
      'Edit all data': false,
      'Delete records': false,
      'Bulk operations': false,
      'System config': false,
      'Manage users': false,
      'Manage roles': false,
      'View applications': false,
      'Approve/reject applications': false,
      'Manage finances': false,
      'View analytics': false,
      'Send communications': false,
      'Manage website': false,
      'Security settings': false,
    }
  },
];

const RolesAdmin = () => {
  const [roles, setRoles] = useState(defaultRoles);
  const [selected, setSelected] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const handlePermissionChange = (roleId, permission, value) => {
    if (roleId === 'super_admin') return; // cannot change super user
    setRoles(prev => prev.map(r => r.id === roleId
      ? { ...r, permissions: { ...r.permissions, [permission]: value } }
      : r
    ));
  };

  const handleSave = () => {
    setSnackbar({ open: true, message: 'Role permissions saved' });
    setEditDialog(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Roles & Permissions</Typography>
          <Typography variant="body2" color="text.secondary">Define what each role can access and do within the system</Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Role Cards */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {roles.map(role => (
              <Card
                key={role.id}
                elevation={selected?.id === role.id ? 3 : 1}
                sx={{
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: selected?.id === role.id ? role.color : 'transparent',
                  transition: 'all 0.2s'
                }}
                onClick={() => setSelected(role)}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: role.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                    {role.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>{role.name}</Typography>
                      {role.id === 'super_admin' && <Chip label="Protected" size="small" sx={{ bgcolor: '#4a148c', color: 'white', fontSize: '0.65rem' }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary">{role.users} user{role.users !== 1 ? 's' : ''}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Grid>

        {/* Permission Detail */}
        <Grid item xs={12} md={8}>
          {selected ? (
            <Paper elevation={2} sx={{ borderRadius: 2, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: selected.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                  {selected.icon}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{selected.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{selected.description}</Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary', textTransform: 'uppercase' }}>Permissions</Typography>

              {selected.id === 'super_admin' && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
                  Super User permissions cannot be modified. This role has unrestricted access to all system functions.
                </Alert>
              )}

              <Grid container spacing={1}>
                {Object.entries(selected.permissions).map(([permission, enabled]) => (
                  <Grid item xs={12} sm={6} key={permission}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1, bgcolor: enabled ? 'success.50' : 'grey.50' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {enabled
                          ? <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                          : <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.disabled' }} />
                        }
                        <Typography variant="body2" sx={{ color: enabled ? 'text.primary' : 'text.disabled' }}>{permission}</Typography>
                      </Box>
                      {selected.id !== 'super_admin' && (
                        <Switch
                          size="small"
                          checked={enabled}
                          onChange={e => {
                            handlePermissionChange(selected.id, permission, e.target.checked);
                            setSelected(prev => ({ ...prev, permissions: { ...prev.permissions, [permission]: e.target.checked } }));
                          }}
                          color="success"
                        />
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {selected.id !== 'super_admin' && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button variant="contained" onClick={handleSave}>Save Permission Changes</Button>
                </Box>
              )}
            </Paper>
          ) : (
            <Paper elevation={1} sx={{ borderRadius: 2, p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <AdminPanelSettings sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
              <Typography variant="body1">Select a role from the left to view and edit its permissions</Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesAdmin;
